import { useReducedMotion } from 'framer-motion'
import { TrendingUp } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  buildTimeSalesChartData,
  hasTimeSalesData,
  type TimeTabView,
} from '@/components/Dashboard/timeTab.utils'
import {
  formatDashboardCurrency,
  formatDashboardNumber,
} from '@/utils/crmValuesDisplay'

const CLOSER_COLORS = ['#7c3aed', '#2563eb', '#0d9488', '#d97706', '#db2777', '#4f46e5']

type Props = {
  view: TimeTabView
  valuesVisible?: boolean
}

export function TimePipelineChart({ view, valuesVisible = false }: Props) {
  const reduced = useReducedMotion()
  const chartData = buildTimeSalesChartData(view).map((row, index) => ({
    ...row,
    fill: CLOSER_COLORS[index % CLOSER_COLORS.length],
  }))

  return (
    <div className='flex h-full flex-col rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-5 shadow-sm'>
      <div className='mb-4'>
        <div className='flex items-center gap-2'>
          <TrendingUp size={16} className='text-accent' aria-hidden />
          <h3 className='text-base font-semibold text-text-primary'>Vendas por closer</h3>
        </div>
        <p className='mt-1 text-sm text-text-muted'>
          Receita fechada no período por responsável
        </p>
      </div>

      {hasTimeSalesData(chartData) ? (
        <>
          <ResponsiveContainer width='100%' height={260}>
            <BarChart data={chartData} layout='vertical' margin={{ left: 8, right: 16 }}>
              <CartesianGrid strokeDasharray='3 3' stroke='var(--border)' horizontal={false} />
              <XAxis
                type='number'
                allowDecimals={false}
                tick={{ fill: 'var(--font-color-muted)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) =>
                  valuesVisible ? formatDashboardCurrency(Number(value), true) : '•••'
                }
              />
              <YAxis
                type='category'
                dataKey='name'
                width={96}
                tick={{ fill: 'var(--font-color-muted)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                }}
                formatter={(value, _name, item) => [
                  `${formatDashboardCurrency(Number(value ?? 0), valuesVisible)} · ${formatDashboardNumber(item.payload.count, valuesVisible)} vendas`,
                  item.payload.name,
                ]}
              />
              <Bar dataKey='value' radius={[0, 6, 6, 0]} isAnimationActive={!reduced} />
            </BarChart>
          </ResponsiveContainer>

          <ul className='mt-3 space-y-2 border-t border-[var(--border)] pt-3'>
            {chartData.map((row) => (
              <li key={row.name} className='flex items-center justify-between gap-3 text-sm'>
                <span className='flex min-w-0 items-center gap-2 text-text-secondary'>
                  <span
                    className='h-2.5 w-2.5 shrink-0 rounded-full'
                    style={{ backgroundColor: row.fill }}
                    aria-hidden
                  />
                  <span className='truncate'>{row.name}</span>
                </span>
                <span className='shrink-0 font-semibold tabular-nums text-text-primary'>
                  {formatDashboardCurrency(row.value, valuesVisible)} ·{' '}
                  {formatDashboardNumber(row.count, valuesVisible)}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className='flex flex-1 flex-col items-center justify-center gap-2 py-12 text-center'>
          <p className='text-sm font-medium text-text-primary'>Nenhuma venda no período</p>
          <p className='max-w-sm text-sm text-text-muted'>
            Ainda não há fechamentos registrados para os closers neste intervalo.
          </p>
        </div>
      )}
    </div>
  )
}
